import {Component, OnDestroy, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {LangChangeEvent, TranslateService} from '@ngx-translate/core';
import {Subscription} from 'rxjs';
import {BaseComponent} from '../../base/base.component';
import {UserService} from '../../service/user.service';
import {FilterService} from '../../service/filter.service';
import {User} from '../../../generated/graphql';
import {
    MOCK_COMPANIES_BY_QUARTER,
    MOCK_COMPANY_STATUS,
    MOCK_KPIS,
    MOCK_STAFF_BY_COMPANY,
    MOCK_STAFF_STATUS_BY_COMPANY,
    MOCK_STATIONS_VS_STAFF
} from './dashboard-physical-security.mock';

declare var moment;
declare var $;

interface KpiCard {
    label: string;
    value: string;
    hint: string;
    variant: 'default' | 'success' | 'warning' | 'danger';
}

@Component({
    selector: 'app-dashboard-physical-security',
    templateUrl: './dashboard-physical-security.component.html',
    styleUrls: ['./dashboard-physical-security.component.scss']
})
export class DashboardPhysicalSecurityComponent extends BaseComponent implements OnInit, OnDestroy {

    filterKey = 'dashboard_physical_filter';
    user: User = new User();

    subscription: Subscription;

    // PhysicalSecurity não tem vínculo com Unity NEM Country (é global) → só filtro de período.
    filter: any = {};

    kpiCards: KpiCard[] = [];

    myData1: any[] = [['', 0]];      // efetivo por empresa
    myData2: any[] = [['', 0]];      // empresas ativas x inativas
    myData3: any[] = [['', 0, 0]];   // postos x efetivo
    myData4: any[] = [['', 0]];      // empresas cadastradas por período
    myData5: any[] = [['', 0, 0]];   // status do efetivo por empresa

    chart1Names = ['Empresa', 'Efetivo'];
    chart1NamesES = ['Empresa', 'Efectivo'];

    chart2Names = ['Status', 'Empresas'];
    chart2NamesES = ['Estado', 'Empresas'];

    chart3Names = ['Empresa', 'Postos', 'Efetivo'];
    chart3NamesES = ['Empresa', 'Puestos', 'Efectivo'];

    chart4Names = ['Trimestre', 'Empresas'];
    chart4NamesES = ['Trimestre', 'Empresas'];

    chart5Names = ['Empresa', 'Ativo', 'Inativo'];
    chart5NamesES = ['Empresa', 'Activo', 'Inactivo'];

    columnNames1: string[] = [];
    columnNames2: string[] = [];
    columnNames3: string[] = [];
    columnNames4: string[] = [];
    columnNames5: string[] = [];

    options1 = {legend: {position: 'none'}, hAxis: {minValue: 0, format: '0', gridlines: {count: -1}}, colors: ['#004B93']};
    options2 = {pieHole: 0.4, legend: {position: 'right'}, colors: ['#032E58', '#53565A']};
    // Agrupado (NÃO empilhado): revela sub/superdimensionamento.
    options3 = {legend: {position: 'top'}, vAxis: {minValue: 0, format: '0', gridlines: {count: -1}}, colors: ['#53565A', '#032E58']};
    options4 = {legend: {position: 'none'}, vAxis: {minValue: 0, format: '0', gridlines: {count: -1}}, colors: ['#004B93']};
    options5 = {
        isStacked: 'percent',
        legend: {position: 'top'},
        vAxis: {minValue: 0, maxValue: 1, format: 'percent', gridlines: {count: 5}},
        colors: ['#032E58', '#C0392B']
    };

    constructor(public router: Router,
                public translate: TranslateService,
                public userService: UserService,
                private filterService: FilterService) {
        super(router, translate);
        this.subscription = this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
            $(() => {
                this.checkSubtitle();
                this.buildKpis();
                this.buildCharts();
            });
        });
    }

    ngOnInit(): void {
        this.userService.user.subscribe({
            next: data => {
                this.user = data as User;
            }, error: err => super.onError(err)
        });

        const savedFilter = this.filterService.getFilter(this.filterKey);
        if (savedFilter) {
            this.filter = savedFilter;
        } else {
            this.setDefaultPeriod();
        }

        this.initDatePicker();

        this.checkSubtitle();
        this.buildKpis();
        this.buildCharts();
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    setDefaultPeriod() {
        this.filter.start = moment().startOf('day').subtract(30, 'days').utcOffset(0, true).format();
        this.filter.end = moment().endOf('day').utcOffset(0, true).format();
    }

    initDatePicker() {
        this.setDatePickerRange('dashboard-daterangepicker', (start, end, label) => {
            this.filter.start = start.utcOffset(0, true).format();
            this.filter.end = end.utcOffset(0, true).format();
        }, this.filter.start, this.filter.end);
    }

    applyFilter() {
        this.filterService.setFilter(this.filterKey, this.filter);

        super.toggleLoader(true);
        this.buildKpis();
        this.buildCharts();
        super.toggleLoader(false);
        // TODO(dados-reais): aplicar filtro
    }

    clearFilter() {
        this.filterService.clearFilter(this.filterKey);
        this.filter = {};
        this.setDefaultPeriod();
    }

    // ---------------------------------------------------------------------
    buildKpis() {
        const k = MOCK_KPIS;
        const t = (key: string) => this.translate.instant('dashboards.physicalSecurity.' + key);
        this.kpiCards = [
            {label: t('kpi.activeCompanies'), value: this.fmtInt(k.activeCompanies), hint: '', variant: 'default'},
            {label: t('kpi.inactiveCompanies'), value: this.fmtInt(k.inactiveCompanies), hint: '', variant: 'default'},
            {label: t('kpi.totalStations'), value: this.fmtInt(k.totalStations), hint: '', variant: 'default'},
            {label: t('kpi.totalStaff'), value: this.fmtInt(k.totalStaff), hint: '', variant: 'default'},
            {label: t('kpi.activeStaff'), value: this.fmtInt(k.activeStaff), hint: '', variant: 'success'},
            {label: t('kpi.coverage'), value: this.fmtMultiplier(k.staffPerStation), hint: '', variant: 'default'},
        ];
    }

    buildCharts() {
        this.myData1 = MOCK_STAFF_BY_COMPANY.map(r => [r.company, r.staff]);
        this.myData2 = MOCK_COMPANY_STATUS.map(r => [this.companyStatusLabel(r.status), r.count]);
        this.myData3 = MOCK_STATIONS_VS_STAFF.map(r => [r.company, r.stations, r.staff]);
        this.myData4 = MOCK_COMPANIES_BY_QUARTER.map(r => [r.quarter, r.count]);
        this.myData5 = MOCK_STAFF_STATUS_BY_COMPANY.map(r => [r.company, r.active, r.inactive]);
    }

    checkSubtitle() {
        if (this.translate.currentLang === 'pt') {
            this.columnNames1 = this.chart1Names;
            this.columnNames2 = this.chart2Names;
            this.columnNames3 = this.chart3Names;
            this.columnNames4 = this.chart4Names;
            this.columnNames5 = this.chart5Names;
        } else {
            this.columnNames1 = this.chart1NamesES;
            this.columnNames2 = this.chart2NamesES;
            this.columnNames3 = this.chart3NamesES;
            this.columnNames4 = this.chart4NamesES;
            this.columnNames5 = this.chart5NamesES;
        }
    }

    companyStatusLabel(status: string): string {
        return this.translate.instant('dashboards.physicalSecurity.status.' + status);
    }

    fmtInt(n: number): string {
        return n.toString();
    }

    fmtMultiplier(n: number): string {
        return n.toFixed(1).replace('.', ',') + 'x';
    }
}
